import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Active when the path matches exactly OR is a sub-route (e.g. /stock/stock-analyze/CME
  // highlights the Analysis tab). Guard against '/' matching everything.
  const isActive = (path) =>
    location.pathname === path || (path !== '/' && location.pathname.startsWith(path + '/'));

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="container-custom">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <span className="text-xl font-bold text-gray-900">GandalStock</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Home is a marketing/landing page — hide it for logged-in users
                (AI Stock Picks is their home; the logo still links to /). */}
            {!user && (
              <Link 
                to="/" 
                className={`nav-link ${isActive('/') ? 'nav-link-active' : ''}`}
              >
                Home
              </Link>
            )}
            {/* AI Stock Picks (login-gated) is shown before Analysis per product order. */}
            {user && (
              <Link 
                to="/strategy" 
                className={`nav-link ${isActive('/strategy') ? 'nav-link-active' : ''}`}
              >
                AI Stock Picks
              </Link>
            )}
            {user && (
              <Link
                to="/ai-rank"
                className={`nav-link ${isActive('/ai-rank') ? 'nav-link-active' : ''}`}
              >
                AI Rank
              </Link>
            )}
            {/* Ask AI is PUBLIC (anonymous allowed; metered per IP/day). */}
            <Link 
              to="/ask-ai" 
              className={`nav-link ${isActive('/ask-ai') ? 'nav-link-active' : ''}`}
            >
              Ask AI
            </Link>
            {/* Analysis is PUBLIC (metered free preview for anonymous visitors), so
                the link is shown to everyone — not just logged-in users. */}
            <Link 
              to="/stock/stock-analyze" 
              className={`nav-link ${isActive('/stock/stock-analyze') ? 'nav-link-active' : ''}`}
            >
              Analysis
            </Link>
            {/* Temporarily hidden - Pricing tab
            <Link 
              to="/pricing" 
              className={`nav-link ${isActive('/pricing') ? 'nav-link-active' : ''}`}
            >
              Pricing
            </Link>
            */}
            
            {user ? (
              <>
                {/* Temporarily hidden - Charts tab
                <Link 
                  to="/charts" 
                  className={`nav-link ${isActive('/charts') ? 'nav-link-active' : ''}`}
                >
                  Charts
                </Link>
                */}
                <Link 
                  to="/watchlist" 
                  className={`nav-link ${isActive('/watchlist') ? 'nav-link-active' : ''}`}
                >
                  Watchlist
                </Link>
                {user.isAdmin && (
                  <Link 
                    to="/admin" 
                    className={`nav-link text-purple-700 font-medium ${isActive('/admin') ? 'nav-link-active' : ''}`}
                  >
                    Admin
                  </Link>
                )}
                
                {/* User Menu */}
                <div className="relative group">
                  <button className="flex items-center space-x-2 nav-link">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {user.firstName?.[0]?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <span>{user.firstName}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <Link to="/account" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Account Settings
                    </Link>
                    <Link to="/billing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Billing
                    </Link>
                    {user.isAdmin && (
                      <Link to="/admin" className="block px-4 py-2 text-sm text-purple-700 font-medium hover:bg-gray-100">
                        Admin
                      </Link>
                    )}
                    <div className="border-t border-gray-100"></div>
                    <button 
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login" className="nav-link">
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary">
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-600 hover:text-gray-900 focus:outline-none"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2">
              {/* Home hidden for logged-in users (marketing page; logo still links to /). */}
              {!user && (
                <Link 
                  to="/" 
                  className="nav-link block"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
              )}
              {/* AI Stock Picks (login-gated) before Analysis per product order. */}
              {user && (
                <Link 
                  to="/strategy" 
                  className="nav-link block"
                  onClick={() => setIsMenuOpen(false)}
                >
                  AI Stock Picks
                </Link>
              )}
              {user && (
                <Link
                  to="/ai-rank"
                  className="nav-link block"
                  onClick={() => setIsMenuOpen(false)}
                >
                  AI Rank
                </Link>
              )}
              {/* Ask AI is PUBLIC (anonymous allowed; metered per IP/day). */}
              <Link 
                to="/ask-ai" 
                className="nav-link block"
                onClick={() => setIsMenuOpen(false)}
              >
                Ask AI
              </Link>
              {/* Analysis is PUBLIC — show to everyone. */}
              <Link 
                to="/stock/stock-analyze" 
                className="nav-link block"
                onClick={() => setIsMenuOpen(false)}
              >
                Analysis
              </Link>
              {/* Temporarily hidden - Pricing tab
              <Link 
                to="/pricing" 
                className="nav-link block"
                onClick={() => setIsMenuOpen(false)}
              >
                Pricing
              </Link>
              */}
              
              {user ? (
                <>
                  {/* Temporarily hidden - Watchlist and Charts tabs
                  <Link 
                    to="/watchlist" 
                    className="nav-link block"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Watchlist
                  </Link>
                  <Link 
                    to="/charts" 
                    className="nav-link block"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Charts
                  </Link>
                  */}
                  <Link 
                    to="/watchlist" 
                    className="nav-link block"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Watchlist
                  </Link>
                  {user.isAdmin && (
                    <Link 
                      to="/admin" 
                      className="nav-link block text-purple-700 font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Admin
                    </Link>
                  )}
                  <Link 
                    to="/account" 
                    className="nav-link block"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Account
                  </Link>
                  <Link 
                    to="/billing" 
                    className="nav-link block"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Billing
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="nav-link block text-left w-full"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="nav-link block"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link 
                    to="/register" 
                    className="btn-primary block text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
